import React, { useMemo } from 'react';
import Badge from '../../../components/ui/Badge';
import { LockIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  ReferenceDataGeneralList,
  type ReferenceInboxColumn,
} from '../../parameters/ReferenceDataGeneralList';
import { companyCanDelete, moduleConfig } from './config';
import { useAccCompanies, type AccCompany } from './useData';

const formatCodeName = (entity?: { code: string; name: string } | null) => {
  if (!entity) return '';
  return entity.name || entity.code || '';
};

const List: React.FC = () => {
  const { colors } = useTheme();
  const formatDate = moduleConfig.utils.formatDate;

  const columns = useMemo<ReferenceInboxColumn<AccCompany>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        className: 'whitespace-nowrap',
        render: (row) => (
          <span className={cn('font-medium font-mono text-xs tracking-wide', colors.text)}>
            {row.code}
          </span>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        render: (row) => (
          <span className={cn('inline-flex items-center gap-1.5 min-w-0 font-medium', colors.text)}>
            <span className="truncate">{row.name}</span>
            {row.usedInRecords ? (
              <span title="Used in journal records" className="inline-flex text-amber-600 shrink-0">
                <LockIcon className="w-4 h-4" />
              </span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'isVessel',
        header: 'Vessel',
        className: 'hidden whitespace-nowrap sm:table-cell',
        render: (row) => (
          <Badge variant={row.isVessel ? 'info' : 'default'}>{row.isVessel ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'isManagementCompany',
        header: 'Management',
        className: 'hidden whitespace-nowrap md:table-cell',
        render: (row) => (
          <Badge variant={row.isManagementCompany ? 'accent' : 'default'}>
            {row.isManagementCompany ? 'Yes' : 'No'}
          </Badge>
        ),
      },
      {
        key: 'managementCompany',
        header: 'Managed by',
        sortable: false,
        className: 'hidden whitespace-nowrap lg:table-cell',
        render: (row) => (
          <span className={colors.textMuted}>
            {row.isVessel ? formatCodeName(row.managementCompany) : ''}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: 'Active',
        className: 'whitespace-nowrap',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        className: 'hidden whitespace-nowrap xl:table-cell',
        render: (row) => <span className={colors.textMuted}>{formatDate(row.createdAt)}</span>,
      },
    ],
    [colors.text, colors.textMuted, formatDate],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useAccCompanies}
      columns={columns}
      defaultSort={{ key: 'code', direction: 'asc' }}
      newLabel="New company"
      getRowLabel={(row) => row.name || row.code}
      canDeleteRow={companyCanDelete}
      loadingLabel="Loading companies…"
    />
  );
};

export default List;
